import { Box, CircularProgress } from "@mui/material"
import { SearchOutlined } from "@mui-symbols-material/w300"
import React, { useMemo, useState } from "react"
import type { FC, UIEvent } from "react"
import { useFormContext } from "react-hook-form"

import { FilterCheckbox } from "@/components/common/Filters"
import { InputField } from "@/components/common/InputField"
import type { ClassificationQueryableFields } from "@/graphql/codegen/graphql"
import useProductClassification from "@/screens/ManageProducts/components/ProductClasses/hooks/useProductClassification"
import useDebounce from "@/utils/useDebounce"

interface ProductClassProps {
  onFilterChange: (name: keyof ClassificationQueryableFields, value: any) => void
}

export const ProductClassFilter: FC<ProductClassProps> = ({ onFilterChange }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const { control, watch } = useFormContext()
  const classificationValue = watch("classIds") || []
  const debouncedSearchString = useDebounce(searchTerm, 300)

  const { productClassList, productClassListFetching, hasMore, loadMore } = useProductClassification({
    query: debouncedSearchString,
  })

  const filteredProductClassList = useMemo(
    () => productClassList.filter((el) => el.name.toUpperCase().includes(searchTerm.toUpperCase())),
    [productClassList, searchTerm]
  )

  const handleFilterChange = (newValue: string[]) => {
    onFilterChange("classIds", newValue)
  }

  const canLoadMore = (element: UIEvent) => {
    const el = element.currentTarget
    return !productClassListFetching && hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight
  }

  return (
    <>
      <Box className='p-4'>
        <InputField
          placeholder='Search by Class Name'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          startAdornment={<SearchOutlined className='text-gray-600' />}
          endAdornment={productClassListFetching && <CircularProgress size={20} />}
        />
      </Box>

      <Box
        className='flex-1 overflow-y-auto overflow-x-hidden px-8'
        onScroll={(event) => {
          if (canLoadMore(event)) {
            loadMore()
          }
        }}
      >
        {filteredProductClassList.map((classification) => (
          <FilterCheckbox
            key={classification.classificationId}
            label={`${classification.name}`}
            value={classification.classificationId}
            control={control}
            name='classIds'
            values={classificationValue}
            onFilterChange={handleFilterChange}
          />
        ))}

        {productClassListFetching && (
          <Box display='flex' justifyContent='center' py={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </>
  )
}
